import type {
  LabelStatus,
  PaymentStatus,
  PrintJobStatus,
  TransitionId,
  WorkflowEventType,
  WorkflowStage,
  WorkflowState,
} from './types'

export interface WorkflowStateDefinition {
  readonly label: string
  readonly stage: WorkflowStage
}

export interface WorkflowTransitionDefinition {
  readonly id: TransitionId
  readonly from: WorkflowState
  readonly to: WorkflowState
  readonly eventType: WorkflowEventType
  readonly requiresHumanApproval: boolean
  readonly idempotent: boolean
  /** Exact contract expressions, kept for contract-drift tests and diagnostics. */
  readonly guards: readonly string[]
}

export const WORKFLOW_STATES = {
  DRAFT: { label: '草稿', stage: 'INTAKE' },
  PRICED: { label: '已确认计价', stage: 'INTAKE' },
  PAID: { label: '已支付', stage: 'PAYMENT' },
  LABEL_ACTIVE: { label: '标签已激活', stage: 'PAYMENT' },
  INTAKE_COMPLETE: { label: '收衣完成', stage: 'INTAKE' },
  SENT_TO_PLANT: { label: '已送洗', stage: 'FULFILLMENT' },
  PLANT_RECEIVED: { label: '已入厂', stage: 'FULFILLMENT' },
  WASHING: { label: '洗护中', stage: 'FULFILLMENT' },
  WAITING_QC: { label: '待质检', stage: 'QC' },
  QC_PASSED: { label: '质检通过', stage: 'QC' },
  REWASH: { label: '返洗', stage: 'QC' },
  EXCEPTION: { label: '异常处理中', stage: 'EXCEPTION' },
  WAITING_SHELF: { label: '待上架', stage: 'SHELVING' },
  SHELVED: { label: '已上架', stage: 'SHELVING' },
  WAITING_PICKUP: { label: '待取衣', stage: 'PICKUP' },
  PICKUP_VERIFYING: { label: '取衣核验中', stage: 'PICKUP' },
  COMPLETED: { label: '已完成', stage: 'COMPLETED' },
} as const satisfies Record<WorkflowState, WorkflowStateDefinition>

export const INITIAL_WORKFLOW_STATE: WorkflowState = 'DRAFT'
export const TERMINAL_WORKFLOW_STATES = ['COMPLETED'] as const satisfies readonly WorkflowState[]

export const WORKFLOW_TRANSITIONS = [
  {
    id: 'CONFIRM_PRICING', from: 'DRAFT', to: 'PRICED', eventType: 'PRICING_CONFIRMED',
    requiresHumanApproval: true, idempotent: false,
    guards: ['pricingBlockers.count == 0', 'allImagesUploaded == true', 'allRequiredRiskAcknowledgementsConfirmed == true'],
  },
  {
    id: 'SIMULATE_PAYMENT', from: 'PRICED', to: 'PAID', eventType: 'SIMULATED_PAYMENT_SUCCEEDED',
    requiresHumanApproval: true, idempotent: false,
    guards: ['payment.status == SUCCEEDED', 'amounts.recalculated == amounts.confirmed'],
  },
  {
    id: 'ACTIVATE_LABELS', from: 'PAID', to: 'LABEL_ACTIVE', eventType: 'LABELS_ACTIVATED',
    requiresHumanApproval: false, idempotent: true,
    guards: ['payment.status == SUCCEEDED', 'activeLabelVersion != null'],
  },
  {
    id: 'COMPLETE_INTAKE', from: 'LABEL_ACTIVE', to: 'INTAKE_COMPLETE', eventType: 'INTAKE_COMPLETED',
    requiresHumanApproval: true, idempotent: false,
    guards: ['printJobs.initialLabels.status in [SUCCEEDED, PARTIAL_FAILED]', 'orderItemCount > 0'],
  },
  {
    id: 'SEND_TO_PLANT', from: 'INTAKE_COMPLETE', to: 'SENT_TO_PLANT', eventType: 'SENT_TO_PLANT',
    requiresHumanApproval: true, idempotent: false, guards: ['dispatchBatchId != null'],
  },
  {
    id: 'RECEIVE_AT_PLANT', from: 'SENT_TO_PLANT', to: 'PLANT_RECEIVED', eventType: 'PLANT_RECEIVED',
    requiresHumanApproval: true, idempotent: false, guards: ['scan.labelStatus == ACTIVE'],
  },
  {
    id: 'START_WASHING', from: 'PLANT_RECEIVED', to: 'WASHING', eventType: 'WASHING_STARTED',
    requiresHumanApproval: true, idempotent: false, guards: [],
  },
  {
    id: 'SEND_TO_QC', from: 'WASHING', to: 'WAITING_QC', eventType: 'WASHING_COMPLETED',
    requiresHumanApproval: true, idempotent: false, guards: ['scanConfirmed == true'],
  },
  {
    id: 'PASS_QC', from: 'WAITING_QC', to: 'QC_PASSED', eventType: 'QC_PASSED',
    requiresHumanApproval: true, idempotent: false,
    guards: ['qc.requiredFindingsUnconfirmed == 0', 'qc.physicalComparisonConfirmed == true'],
  },
  {
    id: 'REQUEST_REWASH', from: 'WAITING_QC', to: 'REWASH', eventType: 'QC_REWASH_REQUESTED',
    requiresHumanApproval: true, idempotent: false, guards: ['qc.note != null'],
  },
  {
    id: 'RESTART_WASHING', from: 'REWASH', to: 'WASHING', eventType: 'REWASH_STARTED',
    requiresHumanApproval: true, idempotent: false, guards: [],
  },
  {
    id: 'READY_FOR_SHELVING', from: 'QC_PASSED', to: 'WAITING_SHELF', eventType: 'READY_FOR_SHELVING',
    requiresHumanApproval: false, idempotent: true, guards: [],
  },
  {
    id: 'BIND_RACK', from: 'WAITING_SHELF', to: 'SHELVED', eventType: 'RACK_BOUND',
    requiresHumanApproval: true, idempotent: false,
    guards: ['rack.status == AVAILABLE', 'garment.activeRackBinding == null', 'garment.status == WAITING_SHELF'],
  },
  {
    id: 'MARK_READY_FOR_PICKUP', from: 'SHELVED', to: 'WAITING_PICKUP', eventType: 'ORDER_READY_FOR_PICKUP',
    requiresHumanApproval: false, idempotent: true, guards: ['allOrderGarments.status == SHELVED'],
  },
  {
    id: 'VERIFY_PICKUP_GARMENT', from: 'WAITING_PICKUP', to: 'PICKUP_VERIFYING', eventType: 'PICKUP_GARMENT_VERIFIED',
    requiresHumanApproval: false, idempotent: true,
    guards: ['pickup.currentScanMatchesOrder == true', 'scan.labelStatus == ACTIVE'],
  },
  {
    id: 'COMPLETE_PICKUP', from: 'PICKUP_VERIFYING', to: 'COMPLETED', eventType: 'PICKUP_COMPLETED',
    requiresHumanApproval: true, idempotent: false,
    guards: ['pickup.verifiedGarmentCount == order.garmentCount', 'pickup.customerIdentityConfirmed == true', 'pickup.riskAndQuantityConfirmed == true'],
  },
] as const satisfies readonly WorkflowTransitionDefinition[]

export const TRANSITIONS_BY_ID: Readonly<Record<TransitionId, WorkflowTransitionDefinition>> =
  Object.freeze(Object.fromEntries(WORKFLOW_TRANSITIONS.map((transition) => [transition.id, transition]))) as unknown as Readonly<Record<TransitionId, WorkflowTransitionDefinition>>

export const EXCEPTION_POLICY = Object.freeze({
  allowedFromStages: ['FULFILLMENT', 'QC', 'SHELVING', 'PICKUP'] as const,
  eventType: 'EXCEPTION_REGISTERED' as const,
  requiresHumanApproval: true,
  resumeStateRequired: true,
  resolutionEventType: 'EXCEPTION_RESOLVED' as const,
})

export const LABEL_STATE_MACHINE = Object.freeze({
  states: ['DRAFT', 'ACTIVE', 'VOID'] as const satisfies readonly LabelStatus[],
  initialState: 'DRAFT' as const,
  transitions: [
    { from: 'DRAFT', to: 'ACTIVE', eventType: 'LABELS_ACTIVATED', guard: 'payment.status == SUCCEEDED' },
    { from: 'ACTIVE', to: 'VOID', eventType: 'LABEL_VOIDED_BY_REPRINT', guard: 'newLabelVersion.status == ACTIVE' },
  ] as const,
})

export const PAYMENT_STATE_MACHINE = Object.freeze({
  states: ['PENDING', 'PROCESSING', 'SUCCEEDED', 'FAILED'] as const satisfies readonly PaymentStatus[],
  initialState: 'PENDING' as const,
  transitions: [
    { from: 'PENDING', to: 'PROCESSING', eventType: 'PAYMENT_STARTED' },
    { from: 'PROCESSING', to: 'SUCCEEDED', eventType: 'SIMULATED_PAYMENT_SUCCEEDED' },
    { from: 'PROCESSING', to: 'FAILED', eventType: 'SIMULATED_PAYMENT_FAILED' },
    { from: 'FAILED', to: 'PROCESSING', eventType: 'PAYMENT_RETRIED' },
  ] as const,
})

export const PRINT_JOB_STATE_MACHINE = Object.freeze({
  states: ['QUEUED', 'PRINTING', 'SUCCEEDED', 'PARTIAL_FAILED', 'FAILED'] as const satisfies readonly PrintJobStatus[],
  initialState: 'QUEUED' as const,
  transitions: [
    { from: 'QUEUED', to: 'PRINTING', eventType: 'PRINT_STARTED' },
    { from: 'PRINTING', to: 'SUCCEEDED', eventType: 'PRINT_SUCCEEDED' },
    { from: 'PRINTING', to: 'PARTIAL_FAILED', eventType: 'PRINT_PARTIAL_FAILED' },
    { from: 'PRINTING', to: 'FAILED', eventType: 'PRINT_FAILED' },
    { from: 'PARTIAL_FAILED', to: 'PRINTING', eventType: 'FAILED_LABELS_RETRIED' },
    { from: 'FAILED', to: 'PRINTING', eventType: 'PRINT_RETRIED' },
  ] as const,
})

export interface TransitionGuardContext {
  readonly pricingBlockersCount?: number
  readonly allImagesUploaded?: boolean
  readonly allRequiredRiskAcknowledgementsConfirmed?: boolean
  readonly paymentStatus?: PaymentStatus
  readonly recalculatedAmount?: number
  readonly confirmedAmount?: number
  readonly activeLabelVersionId?: string
  readonly initialLabelPrintStatus?: PrintJobStatus
  readonly orderItemCount?: number
  readonly dispatchBatchId?: string
  readonly scanLabelStatus?: LabelStatus
  readonly scanConfirmed?: boolean
  readonly qcRequiredFindingsUnconfirmed?: number
  readonly qcPhysicalComparisonConfirmed?: boolean
  readonly qcNote?: string
  readonly rackStatus?: 'AVAILABLE' | 'LOCKED' | 'OCCUPIED'
  readonly hasActiveRackBinding?: boolean
  readonly garmentStatus?: WorkflowState
  readonly allOrderGarmentsStatus?: WorkflowState
  readonly pickupCurrentScanMatchesOrder?: boolean
  readonly pickupVerifiedGarmentCount?: number
  readonly orderGarmentCount?: number
  readonly pickupCustomerIdentityConfirmed?: boolean
  readonly pickupRiskAndQuantityConfirmed?: boolean
}

export interface TransitionCheck {
  readonly allowed: boolean
  readonly transition: WorkflowTransitionDefinition
  readonly failedGuards: readonly string[]
}

export class DomainRuleError extends Error {
  readonly code: string
  readonly details: readonly string[]

  constructor(code: string, message: string, details: readonly string[] = []) {
    super(message)
    this.name = 'DomainRuleError'
    this.code = code
    this.details = details
  }
}

const guardResults = (id: TransitionId, context: TransitionGuardContext): readonly boolean[] => {
  switch (id) {
    case 'CONFIRM_PRICING':
      return [
        context.pricingBlockersCount === 0,
        context.allImagesUploaded === true,
        context.allRequiredRiskAcknowledgementsConfirmed === true,
      ]
    case 'SIMULATE_PAYMENT':
      return [context.paymentStatus === 'SUCCEEDED', context.recalculatedAmount === context.confirmedAmount]
    case 'ACTIVATE_LABELS':
      return [context.paymentStatus === 'SUCCEEDED', Boolean(context.activeLabelVersionId)]
    case 'COMPLETE_INTAKE':
      return [
        context.initialLabelPrintStatus === 'SUCCEEDED' || context.initialLabelPrintStatus === 'PARTIAL_FAILED',
        (context.orderItemCount ?? 0) > 0,
      ]
    case 'SEND_TO_PLANT':
      return [Boolean(context.dispatchBatchId)]
    case 'RECEIVE_AT_PLANT':
      return [context.scanLabelStatus === 'ACTIVE']
    case 'START_WASHING':
    case 'RESTART_WASHING':
    case 'READY_FOR_SHELVING':
      return []
    case 'SEND_TO_QC':
      return [context.scanConfirmed === true]
    case 'PASS_QC':
      return [context.qcRequiredFindingsUnconfirmed === 0, context.qcPhysicalComparisonConfirmed === true]
    case 'REQUEST_REWASH':
      return [Boolean(context.qcNote?.trim())]
    case 'BIND_RACK':
      return [
        context.rackStatus === 'AVAILABLE',
        context.hasActiveRackBinding === false,
        context.garmentStatus === 'WAITING_SHELF',
      ]
    case 'MARK_READY_FOR_PICKUP':
      return [context.allOrderGarmentsStatus === 'SHELVED']
    case 'VERIFY_PICKUP_GARMENT':
      return [context.pickupCurrentScanMatchesOrder === true, context.scanLabelStatus === 'ACTIVE']
    case 'COMPLETE_PICKUP':
      return [
        context.pickupVerifiedGarmentCount === context.orderGarmentCount,
        context.pickupCustomerIdentityConfirmed === true,
        context.pickupRiskAndQuantityConfirmed === true,
      ]
  }
}

export const checkTransition = (
  currentState: WorkflowState,
  transitionId: TransitionId,
  context: TransitionGuardContext,
): TransitionCheck => {
  const transition = TRANSITIONS_BY_ID[transitionId]
  const results = guardResults(transitionId, context)
  const failedGuards = transition.guards.filter((_, index) => results[index] !== true)
  if (currentState !== transition.from) {
    return {
      allowed: false,
      transition,
      failedGuards: [`state == ${transition.from} (actual: ${currentState})`, ...failedGuards],
    }
  }
  return { allowed: failedGuards.length === 0, transition, failedGuards }
}

export const assertTransition = (
  currentState: WorkflowState,
  transitionId: TransitionId,
  context: TransitionGuardContext,
): WorkflowTransitionDefinition => {
  const result = checkTransition(currentState, transitionId, context)
  if (!result.allowed) {
    throw new DomainRuleError(
      'TRANSITION_BLOCKED',
      `${transitionId} cannot transition ${currentState} to ${result.transition.to}`,
      result.failedGuards,
    )
  }
  return result.transition
}

export const canRegisterException = (state: WorkflowState): boolean =>
  EXCEPTION_POLICY.allowedFromStages.includes(WORKFLOW_STATES[state].stage as (typeof EXCEPTION_POLICY.allowedFromStages)[number])
